import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import { LuMenu, LuInfo, LuGlobe, LuBell, LuChevronDown, LuUser, LuLogOut } from "react-icons/lu";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Popover from "@material-ui/core/Popover";
import { getCurrentUserFirstName, getCurrentUserId, getCurrentUserProfileImage, getUserDetails, isSuperAdminLogin, removeAuthObjects } from "../../utils";
import clsx from "clsx";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { getMessage } from "../../containers/User/Messagebox/action";
import { updateUser } from "../../containers/User/UserProfile/action";
import { pageInfoData } from "../../common/pageInfoData";
import PageInfoModal from "../../common/PageInfoModal";
import Tooltip from "@material-ui/core/Tooltip";
import CustomShowModal from "../Common/CustomShowModal";
import WorkflowDiagram from "../../containers/User/Welcome/WorkflowDiagram";
import EstimationWorkflowDiagram from "../../containers/User/Welcome/EstimationWorkflowDiagram";


const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: "#41465f",
  },
  menuButton: {
    marginRight: theme.spacing(1),
  },
  title: {
    flexGrow: 1,
  },
  'MuiList-padding': {
    padding: 0,
  },
}));

export default function ButtonAppBar(props) {
  const {
    title = "",
    onClickMenu = () => { },
    validRoutes = [],
    activeRoute = "",
  } = props;

  const classes = useStyles();
  const userDetails = getUserDetails();
  const [allUnreadNotifications, setAllUnreadNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [isPageInfoOpen, setIsPageInfoOpen] = useState(false);
  const [isWorkflowOpen, setIsWorkflowOpen] = useState(false);
  const [isEstimationWorkflowOpen, setIsEstimationWorkflowOpen] = useState(false);
  const history = useHistory();

  const goToRoute = (path) => history.push(path);


  const handleProfileContainerClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setNotificationAnchor(null);
  };

  const handleProfileClick = () => {
    history.push("/profile");
    handleClose();
  };

  const handleLogoutClick = () => {
    removeAuthObjects();
    history.push("/");
    handleClose();
  };

  const flattenUnreadMessages = (messages) => {
    const unreadMessages = [];
    const currentUserId = getCurrentUserId();

    messages.forEach((message) => {
      // Check if the main message is unread and not sent by current user
      if (!message.isRead && message.sendedBy?._id !== currentUserId) {
        unreadMessages.push({
          ...message,
          isReply: false,
          parentMessageId: null,
        });
      }

      // Check for unread replies
      if (message.replies && message.replies.length > 0) {
        message.replies.forEach((reply) => {
          if (!reply.isRead && reply.sendedBy?._id !== currentUserId) {
            unreadMessages.push({
              ...reply,
              isReply: true,
              parentMessageId: message._id,
            });
          }
        });
      }
    });

    return unreadMessages;
  };

  const fetchMessages = async () => {
    const response = await getMessage({ id: getCurrentUserId() });
    if (response?.data) {
      const allMessages = [
        ...response.data.myInbox,
        ...response.data.sendedMsg,
      ];
      const unreadMessages = flattenUnreadMessages(allMessages);
      setAllUnreadNotifications(unreadMessages);
    }
  };

  const handleNotificationItemClick = (notif) => {
    // Navigate to the parent message if it's a reply, otherwise to the message itself
    const messageId = notif.isReply ? notif.parentMessageId : notif._id;
    history.push(`/messagebox?id=${messageId}`);

    // Remove the clicked notification from the list
    setAllUnreadNotifications((prev) =>
      prev.filter((item) => item._id !== notif._id)
    );
    handleClose();
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchMessages, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    window.isPageInfoModalOpen = isPageInfoOpen;
    if (!isPageInfoOpen) {
      window.isPageInfoModalScheduled = false;
    }
    window.dispatchEvent(new CustomEvent("pageInfoModalToggle", { detail: isPageInfoOpen }));
  }, [isPageInfoOpen]);

  useEffect(() => {
    let timerId;
    if (activeRoute && pageInfoData[activeRoute]) {
      try {
        const authDataString = localStorage.getItem("auth");
        if (authDataString) {
          const authData = JSON.parse(authDataString);
          const visitedRoutes = authData.visitedRoutes || [];

          if (!visitedRoutes.includes(activeRoute)) {
            window.isPageInfoModalScheduled = true;
            timerId = setTimeout(() => {
              setIsPageInfoOpen(true);
            }, 1000); // 1s delay

            const newVisitedRoutes = [...visitedRoutes, activeRoute];
            const newAuthData = { ...authData, visitedRoutes: newVisitedRoutes };
            localStorage.setItem("auth", JSON.stringify(newAuthData));

            updateUser({ _id: getCurrentUserId(), visitedRoutes: newVisitedRoutes })
              .catch(error => {
                console.error("Failed to update user profile with visited routes:", error);
              });
          }
        }
      } catch (error) {
        console.error("Failed to handle page info modal logic", error);
      }
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [activeRoute]); return (
    <AppBar position="static" className="bg-white border-b border-gray-200 text-gray-800 sticky top-0 left-0 right-0 w-full shadow-none" style={{ zIndex: 999999999 }}>
      <Toolbar className="min-h-[56px] px-3 md:px-4 py-1" style={{ minHeight: '56px' }}>
        <div className="appbar-content flex justify-between w-full min-w-0 items-center gap-2">

          {/* Left Side */}
          <div className="appbar-title-group flex flex-1 items-center gap-2 min-w-0">
            <button
              onClick={onClickMenu}
              className="appbar-btn w-8 h-8 flex items-center justify-center rounded-full bg-[#f8fafc] hover:bg-slate-100 text-slate-500 transition-colors"
              aria-label="menu"
            >
              <LuMenu size={18} />
            </button>

            <Typography
              className={clsx(classes.title, "flex items-center gap-1.5 min-w-0 font-bold text-[15px] sm:text-xl text-[#1a2836] whitespace-nowrap")}
            >
              <span className="truncate">{title}</span>
              {(pageInfoData[activeRoute]) && (
                <button
                  onClick={() => setIsPageInfoOpen(true)}
                  className="text-slate-400 hover:text-blue-500 transition-colors flex items-center justify-center ml-1 shrink-0"
                  aria-label="page-info"
                >
                  <LuInfo size={16} />
                </button>
              )}
            </Typography>
          </div>

          {/* Right Side - Profile & Notifications */}
          <div className="appbar-actions flex gap-1 sm:gap-4 md:gap-6 items-center shrink-0">
            {/* CFC & EL Badges */}
            {!isSuperAdminLogin() && <div className="flex gap-1 md:gap-2 items-center">
              <Tooltip title="Estimation Lifecycle (EL)" arrow>
                <button
                  onClick={() => setIsEstimationWorkflowOpen(true)}
                  className="flex items-center justify-center px-1.5 sm:px-2.5 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[10px] transition-all border border-slate-200 hover:scale-105 active:scale-95 shadow-sm uppercase"
                >
                  EL
                </button>
              </Tooltip>
              <Tooltip title="Contract Flow Chart (CFC)" arrow>
                <button
                  onClick={() => setIsWorkflowOpen(true)}
                  className="flex items-center justify-center px-1.5 sm:px-2.5 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[10px] transition-all border border-slate-200 hover:scale-105 active:scale-95 shadow-sm uppercase"
                >
                  CFC
                </button>
              </Tooltip>
            </div>}


            {/* Vertical Divider */}
            <div className="w-px h-5 bg-slate-200 mx-0.5 hidden sm:block"></div>

            {/* Notifications */}
            {
              !window.location.href.includes("/messagebox") && (
                <div className="relative flex items-center justify-center">
                  <button
                    type="button"
                    aria-label="Notifications"
                    aria-haspopup="dialog"
                    aria-expanded={Boolean(notificationAnchor)}
                    aria-controls={notificationAnchor ? "notifications-popover" : undefined}
                    className="p-1.5 text-slate-500 hover:text-slate-700 transition-colors relative"
                    onClick={handleNotificationClick}
                  >
                    {allUnreadNotifications.length > 0 && (
                      <span className="absolute top-0.5 right-0.5 bg-[#f43f5e] text-white w-3.5 h-3.5 flex items-center justify-center text-[9px] font-bold rounded-full">
                        {allUnreadNotifications.length}
                      </span>
                    )}
                    <LuBell size={18} />
                  </button>

                  <Popover
                    id="notifications-popover"
                    anchorEl={notificationAnchor}
                    open={Boolean(notificationAnchor)}
                    onClose={handleClose}
                    PaperProps={{
                      role: "dialog",
                      "aria-labelledby": "notifications-title",
                      className: "compact-popover flex flex-col w-80 max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] overflow-hidden",
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    {/* Header */}
                    <div className="shrink-0 px-3 py-2 border-b border-slate-200 bg-slate-50">
                      <h3 id="notifications-title" className="text-xs font-semibold text-slate-900">Notifications</h3>
                    </div>

                    {/* Notifications List */}
                    <div className="min-h-0 max-h-80 overflow-y-auto scroll-thin">
                      {allUnreadNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-5 px-3 text-center">
                          <LuBell size={20} className="text-slate-400 mb-2" aria-hidden="true" />
                          <p className="text-xs font-medium text-slate-900 mb-1">No notifications</p>
                          <p className="text-[11px] text-slate-500">We'll notify you when something arrives</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-200">
                          {allUnreadNotifications.map((notif) => (
                            <button
                              key={notif._id}
                              type="button"
                              onClick={() => handleNotificationItemClick(notif)}
                              className="block w-full px-3 py-2 text-left hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500 transition-colors group"
                            >
                              <div className="flex gap-2">
                                {/* Avatar */}
                                <div className="flex-shrink-0">
                                  <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center text-[11px] font-medium text-slate-700">
                                    {notif.sendedBy?.firstName?.[0]}{notif.sendedBy?.lastName?.[0]}
                                  </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      {notif.isReply && (
                                        <span className="text-[11px] text-sky-600 font-semibold">Reply: </span>
                                      )}
                                      <p className="text-xs font-medium text-slate-900 leading-4 line-clamp-2 group-hover:text-sky-700 transition-colors">
                                        {notif.messageTitle}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-between gap-1 mt-1">
                                    <div className="flex items-center gap-1 min-w-0">
                                      <span className="text-[11px] text-slate-500">From:</span>
                                      <span className="text-[11px] font-medium text-slate-700 truncate">
                                        {notif.sendedBy?.firstName} {notif.sendedBy?.lastName}
                                      </span>
                                    </div>
                                    <span className="text-[11px] text-slate-500 whitespace-nowrap">
                                      {new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </Popover>
                </div>
              )
            }
            <div
              id="profile-menu-button"
              role="button"
              tabIndex={0}
              aria-label="Account menu"
              aria-haspopup="menu"
              aria-expanded={Boolean(anchorEl)}
              aria-controls={anchorEl ? "profile-menu" : undefined}
              onClick={handleProfileContainerClick}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleProfileContainerClick(event);
                }
              }}
              className="appbar-profile flex gap-1.5 sm:gap-2.5 items-center cursor-pointer group ml-0 sm:ml-1 min-w-0 focus-visible:ring-2 focus-visible:ring-slate-500"
            >
              <div className="w-8 h-8 rounded-full overflow-hidden bg-[#ffcc00] flex items-center justify-center text-sm font-bold shadow-sm">
                {getCurrentUserProfileImage() ? (
                  <img
                    src={getCurrentUserProfileImage()}
                    alt="User Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={`https://ui-avatars.com/api/?name=${getCurrentUserFirstName()}&background=ffcc00&color=000&bold=true`}
                    alt="User Profile"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="flex flex-col min-w-0">
                  <span className="appbar-user-name text-[13px] font-bold text-[#1a2836] leading-none">{getCurrentUserFirstName()}</span>
                  <span className="appbar-user-role text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5 leading-none">
                    {userDetails?.role || 'SUPERBOSS'}
                  </span>
                </div>
                <LuChevronDown size={14} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
              </div>
            </div>

          </div>

          {/* Profile Dropdown Menu */}
          <Menu
            id="profile-menu"
            MenuListProps={{ "aria-labelledby": "profile-menu-button" }}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              className: "compact-popover action-menu-paper",
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <span className="action-menu-icon" aria-hidden="true"><LuUser size={16} /></span>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogoutClick} className="!text-rose-600 hover:!bg-rose-50">
              <span className="action-menu-icon" aria-hidden="true"><LuLogOut size={16} /></span>
              Logout
            </MenuItem>
          </Menu>
        </div>
      </Toolbar>

      {/* Global Page Info Modal */}
      {(pageInfoData[activeRoute]) && (
        <PageInfoModal
          isOpen={isPageInfoOpen}
          onClose={() => setIsPageInfoOpen(false)}
          title={pageInfoData[activeRoute]?.title}
          steps={pageInfoData[activeRoute]?.steps}
          tabs={pageInfoData[activeRoute]?.tabs}
        />
      )}

      {/* Global Workflow Diagrams */}
      <CustomShowModal
        open={isWorkflowOpen}
        handleClose={() => setIsWorkflowOpen(false)}
        title="Contract Flow Chart"
        maxWidth="lg"
      >
        {isWorkflowOpen && (
          <WorkflowDiagram
            goToRoute={goToRoute}
            onClose={() => setIsWorkflowOpen(false)}
          />
        )}
      </CustomShowModal>

      <CustomShowModal
        open={isEstimationWorkflowOpen}
        handleClose={() => setIsEstimationWorkflowOpen(false)}
        title="Estimation Lifecycle"
        maxWidth="lg"
      >
        {isEstimationWorkflowOpen && (
          <EstimationWorkflowDiagram
            goToRoute={goToRoute}
            onClose={() => setIsEstimationWorkflowOpen(false)}
          />
        )}
      </CustomShowModal>

    </AppBar>
  );
}
